/**
 * The programme lead's decision on a submitted module, or the reopening of an
 * approved one while the programme is still open for editing.
 *
 * @module components/studio/ReviewActions
 */

import React from "react";

import type { ModuleEditorState } from "@/components/studio/useModuleEditor";
import { Button, Panel, useConfirm } from "@/components/ui";
import { FormField, TextAreaField } from "@/components/ui/Form";

export function ReviewActions({ editor }: { editor: ModuleEditorState }): React.ReactElement {
  const confirm = useConfirm();
  const busy = editor.reviewAction !== null;
  // The decision in flight outranks the note: it refuses every control here,
  // including the one a missing note would not.
  const busyReason = busy ? "Wait for the decision in progress to finish." : undefined;
  const noteMissing = !editor.reviewNote.trim();
  const noteReason = busyReason ?? (noteMissing ? "Write a review note first." : undefined);
  // The server offers the review of an approved module only to reopen it.
  const reopening = editor.record?.moduleReview.status === "Approved";

  return (
    <Panel
      title="Programme lead review"
      icon="clipboard-text"
      headingLevel={3}
      data-testid="module-review-controls"
    >
      <div className="max-w-2xl">
        <FormField
          label="Review note"
          htmlFor="module-review-note"
          helpText={
            reopening
              ? "Say what the module needs to change. Its builder sees this note."
              : "Give your reasons for approving, or say what needs to change. You must add a note to return or reject a module."
          }
        >
          <TextAreaField
            id="module-review-note"
            className="min-h-24"
            value={editor.reviewNote}
            onChange={editor.setReviewNote}
            placeholder="Required when requesting changes"
            disabled={busy}
            disabledReason={busyReason}
            data-testid="module-review-note"
          />
        </FormField>
      </div>
      {reopening ? (
        <div className="mt-3 flex flex-wrap gap-2">
          <Button
            variant="outline"
            icon="arrow-counter-clockwise"
            disabled={noteMissing || busy}
            disabledReason={noteReason}
            onClick={() => editor.reviewModule("reopen")}
            data-testid="module-reopen"
          >
            Reopen for changes
          </Button>
        </div>
      ) : (
        <div className="mt-3 flex flex-wrap gap-2">
          <Button
            variant="outline"
            icon="arrow-u-up-left"
            disabled={noteMissing || busy}
            disabledReason={noteReason}
            onClick={() => editor.reviewModule("request-changes")}
            data-testid="module-request-changes"
          >
            Request changes
          </Button>
          <Button
            variant="outline"
            icon="chat-text"
            disabled={noteMissing || busy}
            disabledReason={noteReason}
            onClick={() => editor.reviewModule("request-suggestions")}
            data-testid="module-request-suggestions"
          >
            Return with suggestions
          </Button>
          <Button
            variant="outline"
            tone="danger"
            icon="x-circle"
            disabled={noteMissing || busy}
            disabledReason={noteReason}
            onClick={() => editor.reviewModule("not-approve")}
            data-testid="module-not-approve"
          >
            Do not approve
          </Button>
          <Button
            variant="primary"
            icon="check-circle"
            disabled={busy}
            disabledReason={busyReason}
            onClick={async () => {
              // The server refuses every later decision on an approved module,
              // and the module itself becomes read-only, so this one asks.
              const confirmed = await confirm({
                title: "Approve this module?",
                body: "The module becomes read-only. While the programme is open for editing, you can reopen it for changes.",
                confirmLabel: "Approve module",
                "data-testid": "module-approval-confirmation",
              });
              if (confirmed) {
                await editor.reviewModule("approve");
              }
            }}
            data-testid="module-approve"
          >
            Approve module
          </Button>
        </div>
      )}
    </Panel>
  );
}

export default ReviewActions;
